import { Component, CUSTOM_ELEMENTS_SCHEMA, Input, EventEmitter, Output } from '@angular/core';
import { RouterModule } from '@angular/router';

import { FormGroup } from '@angular/forms';
import { debounceTime, distinctUntilChanged, ReplaySubject, takeUntil } from 'rxjs';
import '@maersk-global/mds-components-core/mc-pagination';
import '@maersk-global/mds-components-core/mc-loading-indicator';
import '@maersk-global/mds-components-core/mc-button';
import '@maersk-global/mds-components-core/mc-icon';
import '@maersk-global/mds-components-core/mc-table';
import { WorkOrderService } from '../../../services/work-order.service';
import { MaintenanceOrderHeader } from '../../../models/maintenance-order-header.model';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { WorkOrderFilterForm } from '../../../form-model/work-order-filter.form.model';
import { MaintenanceOrderFilter } from '../../../models/maintenance-order-filter.model';
import { WorkOrderSearchTextForm } from '../../../form-model/work-order-search-text.form.model';
import { LoaderComponent } from '../../../shared-components/loader/loader.component';
import { APICallStatus } from '../../../models/api-call-status.mode';
import { BulkWorkOrderCompletionBody } from '../../../models/bulk-work-order/bulk-work-order-completion.model';
import { MaintenanceOrderStatus } from '../../../models/maintenance-order-status.model';
import { SharedDataService } from '../../../services/shared-data.service';
import { shopManagementGridColumns } from './shop-management-grid.config';
import { ShopManagementFilterForm } from '../../../form-model/shop-management-filter.form.model';
import { MasterDataManagementService } from '../../../services/master-data-management.service';
import { ShopManagement } from '../../../models/shop-management.model';
import { ShopManagementFilter } from '../../../models/shop-management-filter.model';

@Component({
  selector: 'app-shop-management-grid',
  imports: [NgxSkeletonLoaderModule, RouterModule, LoaderComponent],
  templateUrl: './shop-management-grid.component.html',
  styleUrl: './shop-management-grid.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class WorkOrderGridComponent {
  selected = [''];
  selectedRows: BulkWorkOrderCompletionBody[] = [];
  data: ShopManagement[] = [];
  mockDataForLoadingAnimation: { id: number }[] = [...Array(30).keys()].map((i) => ({ id: i }));
  firstPage: number | undefined;
  previousPage: number | undefined;
  currentPage: number = 1;
  nextPage: number | undefined;
  lastPage: number | undefined;
  totalItemCount: number | undefined;
  apiVersion: string | undefined;
  pageLimit: number = 20;
  sort: string | undefined;
  filterParam: ShopManagementFilter = {};
  getShopCodeApiCallStatus: APICallStatus = 'NOT_STARTED';
  hasData: boolean = false;
  urlNavigate: string | undefined;
  @Input({ required: true }) filterFormGroup!: FormGroup<{
    shopManagementFilterForm: FormGroup<ShopManagementFilterForm>;
    workOrderSearchTextForm: FormGroup<WorkOrderSearchTextForm>;
  }>;
  @Output() selectedRowsChange = new EventEmitter<string[]>(); // Emit the selected rows
  shopManagementColumns = shopManagementGridColumns;

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  @Input() isWorkOrderList: boolean = true;
  constructor(
    private masterDataManagement: MasterDataManagementService,
    private sharedDataService: SharedDataService,
  ) {}

  ngOnInit() {
    this.sort = `updatedAt:desc`;
    // Navigate within Shop Management to shop-specific view
    this.urlNavigate = '/master-data/shop-management';
    this.getShopData();
    this.subscribeToFormChanges();
    //this.filterParam.isProcessedByRuleEngine = !this.isWorkOrderList;
    // fetch work orders for if work order page, else for approval screen work orders with default filters need to fetched
    /*if(this.isWorkOrderList && !this.sharedDataService.getWorkOrderFilterFormValue()){
    this.getWorkOrder();
    }*/
  }
  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  private subscribeToFormChanges() {
    this.filterFormGroup.valueChanges.pipe(debounceTime(500), distinctUntilChanged()).subscribe(() => {
      if (
        this.filterFormGroup.controls.shopManagementFilterForm.controls.applyShopManagementFilter.value ||
        this.filterFormGroup.controls.workOrderSearchTextForm.controls.appliedWOSearchTextFilter.value
      ) {
        this.onApplyFilter();
      }
    });
  }
  onSortDirectionChange(event: Event) {
    let customEvent = event as CustomEvent;
    let sortColumn: string = customEvent.detail.column;
    let sortDirection: string = customEvent.detail.direction == 'ascending' ? 'asc' : 'desc';
    if (sortColumn != 'id') {
      this.sort = `${sortColumn}:${sortDirection}`;
      this.currentPage = 1;
      this.getShopData();
    }
  }

  onPageLimitChange(event: Event) {
    let customEvent = event as CustomEvent;
    this.currentPage = 1;
    this.pageLimit = customEvent.detail.value;

    this.getShopData();
  }

  onPageChange(event: Event) {
    let customEvent = event as CustomEvent;
    let changedPage = customEvent.detail as number;
    if (changedPage != this.currentPage) {
      this.currentPage = changedPage;
      this.getShopData(true);
    }
  }

  onApplyFilter() {
    if (this.filterFormGroup.controls.shopManagementFilterForm.valid) {
      const filters: ShopManagementFilter = {};

      if (
        this.filterFormGroup.controls.shopManagementFilterForm.controls.shopCode.value &&
        this.filterFormGroup.controls.shopManagementFilterForm.controls.shopCode.value.length > 0
      ) {
        filters.shopCodesList = this.filterFormGroup.controls.shopManagementFilterForm.controls.shopCode.value;
      }
      if (
        this.filterFormGroup.controls.shopManagementFilterForm.controls.partSupplierType.value &&
        this.filterFormGroup.controls.shopManagementFilterForm.controls.partSupplierType.value.length > 0
      ) {
        filters.partSupplierTypesList = this.filterFormGroup.controls.shopManagementFilterForm.controls.partSupplierType.value;
      }
      if (
        this.filterFormGroup.controls.shopManagementFilterForm.controls.locationCode.value &&
        this.filterFormGroup.controls.shopManagementFilterForm.controls.locationCode.value.length > 0
      ) {
        filters.locationCodeList = this.filterFormGroup.controls.shopManagementFilterForm.controls.locationCode.value;
      }
      if (
        this.filterFormGroup.controls.shopManagementFilterForm.controls.isActive.value &&
        this.filterFormGroup.controls.shopManagementFilterForm.controls.isActive.value.length > 0
      ) {
        filters.shopActiveStatusesList = this.filterFormGroup.controls.shopManagementFilterForm.controls.isActive.value;
      }
      if (this.filterFormGroup.controls.workOrderSearchTextForm.controls.searchText.value) {
        filters.searchText = this.filterFormGroup.controls.workOrderSearchTextForm.controls.searchText.value;
      }

      this.filterParam = filters;
      this.currentPage = 1;
      this.getShopData();
    }
  }

  getShopData(isPageChange: boolean = false): void {
    this.getShopCodeApiCallStatus = 'IN_PROGRESS';
    //this.filterParam.isProcessedByRuleEngine = !this.isWorkOrderList;
    this.masterDataManagement
      .getShopManagement(this.currentPage, this.pageLimit, this.sort, this.filterParam)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response) => {
          const headers = response.headers;
          const body = response.body || [];
          this.data = body.map((item) => ({
            ...item,
            id: item.shopCode,
            isActive: item.isActive,
            isActiveDisplay: item.isActive ? 'Active' : 'Inactive',
          }));
          this.firstPage = headers.get('First-Page') ? +headers.get('First-Page')! : undefined;
          this.previousPage = headers.get('Previous-Page') ? +headers.get('Previous-Page')! : undefined;
          this.currentPage = +headers.get('Current-Page')!;
          this.nextPage = headers.get('Next-Page') ? +headers.get('Next-Page')! : undefined;
          this.lastPage = headers.get('Last-Page') ? +headers.get('Last-Page')! : undefined;
          this.totalItemCount = headers.get('Total-Item-Count') ? +headers.get('Total-Item-Count')! : undefined;
          this.apiVersion = headers.get('API-Version') ?? undefined;
          this.getShopCodeApiCallStatus = 'COMPLETED_SUCCESFULLY';
        },
        error: (err) => {
          this.getShopCodeApiCallStatus = 'FAILED';
          this.data = [];
        },
      });
  }

  clearFilter() {
    this.filterFormGroup.reset();
    this.filterFormGroup.controls.workOrderSearchTextForm.controls.appliedWOSearchTextFilter.setValue(true);
    this.filterFormGroup.controls.shopManagementFilterForm.controls.applyShopManagementFilter.setValue(true);
  }
}
